import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../context/AuthContext';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

// Module-level guard: survives React StrictMode double-mount
// so the one-time session_id is not consumed twice.
let processedSessionId = null;
let processedPromise = null;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const hash = window.location.hash || '';
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setError('No se encontró session_id en la URL.');
      return;
    }

    const run = async () => {
      // If already processing/processed this exact session_id, await the same promise
      if (processedSessionId === sessionId && processedPromise) {
        try {
          const user = await processedPromise;
          setUser(user);
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/admin', { replace: true, state: { user } });
        } catch (e) {
          const msg = e.response?.data?.detail || e.message || 'Error de autenticación';
          setError(msg);
        }
        return;
      }

      processedSessionId = sessionId;
      processedPromise = (async () => {
        const res = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        return res.data.user;
      })();

      try {
        const user = await processedPromise;
        setUser(user);
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/admin', { replace: true, state: { user } });
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || 'Error de autenticación';
        setError(msg);
        // allow retry by clearing the promise but keep the id to avoid immediate re-fire
        processedPromise = null;
      }
    };

    run();
  }, [navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-6">
        <div className="max-w-md text-center bg-white border border-neutral-200 rounded-2xl p-10">
          <div className="w-14 h-14 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="w-7 h-7" />
          </div>
          <h1 className="text-xl font-semibold mb-2">Acceso no autorizado</h1>
          <p className="text-neutral-600 text-sm mb-6">{error}</p>
          <Button onClick={() => { processedSessionId = null; processedPromise = null; navigate('/admin/login', { replace: true }); }} className="bg-[#07301f] hover:bg-[#0a4a31] text-white">
            Volver al inicio de sesión
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="flex flex-col items-center gap-3 text-neutral-600">
        <Loader2 className="w-8 h-8 animate-spin text-[#07301f]" />
        <p className="text-sm">Verificando sesión…</p>
      </div>
    </div>
  );
}
